import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Mail, CheckCircle2, X, Edit3, Send, Plus, Trash2, CreditCard } from 'lucide-react';import { toast } from 'sonner';
import { adminApi, money, fmtDate, statusPill, statusLabel, API } from '../../lib/api';

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [products, setProducts] = useState([]);
  const [showPay, setShowPay] = useState(false);
  const [payAmt, setPayAmt] = useState(0);
  const [payMethod, setPayMethod] = useState('Bank Transfer');
  const [payNote, setPayNote] = useState('');
  const [payReference, setPayReference] = useState('');

  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get(`/admin/invoices/${id}`).then((r) => setInv(r.data));
  useEffect(() => {
    load();
    adminApi.get('/admin/products').then((r) => setProducts(r.data));
  }, [id]);

  if (!inv)
    return <div className="py-20 text-center text-sm text-[#6b7280]">Loading...</div>;

  const balance = (inv.total || 0) - (inv.paid_amount || 0);
  const isFinalized = !['draft', 'cancelled'].includes(inv.status);
  const canEdit = !['paid', 'cancelled'].includes(inv.status);

  const confirm = async () => {
    try {
      await adminApi.post(`/admin/invoices/${id}/confirm`);
      toast.success('Invoice confirmed & emailed to customer');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };
  const resend = async () => {
    if (!window.confirm('Re-send the updated invoice PDF to the customer via email?')) return;
    try {
      await adminApi.post(`/admin/invoices/${id}/resend`);
      toast.success('Invoice re-sent to customer');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };
  const markPaid = async () => {
    const amt = Number(payAmt);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    const noteCombined = [payReference ? `Ref: ${payReference}` : '', payNote]
      .filter(Boolean)
      .join(' — ');
    try {
      await adminApi.post(`/admin/invoices/${id}/mark-paid`, {
        amount: amt,
        method: payMethod,
        note: noteCombined,
      });
      toast.success('Payment recorded');
      setShowPay(false);
      setPayAmt(0);
      setPayNote('');
      setPayReference('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };
  const sendReminder = async () => {
    try {
      await adminApi.post(`/admin/invoices/${id}/send-reminder`);
      toast.success('Reminder sent');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const syncStripe = async () => {
    const t = toast.loading('Checking Stripe…');
    try {
      const r = await adminApi.post(`/admin/invoices/${id}/sync-stripe`);
      toast.dismiss(t);
      if (r.data.applied) {
        toast.success(`Stripe payment of $${r.data.amount} applied — invoice is now ${r.data.new_status}`);
        load();
      } else {
        toast.message(`Stripe says: ${r.data.stripe_payment_status} — nothing to apply (${r.data.reason || 'already in sync'})`);
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || 'Stripe sync failed');
    }
  };
  const download = async () => {
    const token = localStorage.getItem('adminToken');
    const resp = await fetch(`${API}/admin/invoices/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${inv.invoice_number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteInvoice = async () => {
    const hasPayments = (inv.paid_amount || 0) > 0;
    const msg = hasPayments
      ? `Invoice ${inv.invoice_number} has ${money(inv.paid_amount)} in recorded payments.\n\nDelete anyway? This will also remove the payment records.`
      : `Delete invoice ${inv.invoice_number}?\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await adminApi.delete(`/admin/invoices/${id}${hasPayments ? '?force=true' : ''}`);
      toast.success(`Deleted ${inv.invoice_number}`);
      navigate('/admin/orders');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const startEdit = () => {
    setEditItems(
      (inv.items || []).map((it) => ({
        product_id: it.product_id || '',
        name: it.name,
        qty: it.qty,
        unit_price: it.unit_price,
      })),
    );
    setEditNotes(inv.notes || '');
    setEditDueDate(inv.due_date ? inv.due_date.slice(0, 10) : '');
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);

  const addLine = () =>
    setEditItems([...editItems, { product_id: '', name: '', qty: 1, unit_price: 0 }]);
  const removeLine = (i) => setEditItems(editItems.filter((_, idx) => idx !== i));
  const updateLine = (i, field, value) => {
    const next = [...editItems];
    next[i] = { ...next[i], [field]: value };
    if (field === 'product_id') {
      const p = products.find((pp) => pp.product_id === value);
      if (p) {
        next[i].name = p.name;
        if (!next[i].unit_price) next[i].unit_price = p.default_price;
      }
    }
    setEditItems(next);
  };
  const save = async () => {
    if (editItems.length === 0) return toast.error('At least one line item required');
    const clean = editItems.map((it) => ({
      product_id: it.product_id || '',
      name: it.name,
      qty: Number(it.qty) || 0,
      unit_price: Number(it.unit_price) || 0,
    }));
    if (clean.some((it) => !it.name || it.qty <= 0))
      return toast.error('Each line needs a name and positive qty');
    setSaving(true);
    try {
      const body = { items: clean, notes: editNotes };
      if (editDueDate) body.due_date = new Date(editDueDate).toISOString();
      await adminApi.patch(`/admin/invoices/${id}`, body);
      toast.success('Invoice updated');
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const editSubtotal = editItems.reduce(
    (s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0),
    0,
  );

  return (
    <div className="fade-in space-y-6" data-testid="invoice-detail-page">
      <button
        onClick={() => navigate('/admin/orders')}
        className="text-sm text-[#6b7280] hover:text-[#0a0a0a] flex items-center gap-1"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-semibold mono">{inv.invoice_number}</h1>
            <span className={statusPill(inv.status)}>{statusLabel(inv.status)}</span>
          </div>
          <div className="text-sm text-[#6b7280] mt-1">
            {inv.customer_snapshot?.name} · {inv.customer_snapshot?.email}
          </div>
        </div>
        {!editing && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={download} className="btn-ghost flex items-center gap-2" data-testid="download-invoice-btn">
              <Download size={14} /> PDF
            </button>
            {canEdit && (
              <button onClick={startEdit} className="btn-ghost flex items-center gap-2" data-testid="edit-invoice-btn">
                <Edit3 size={14} /> Edit
              </button>
            )}
            <button
              onClick={deleteInvoice}
              className="btn-ghost text-[#d92027] flex items-center gap-2"
              data-testid="delete-invoice-btn"
            >
              <Trash2 size={14} /> Delete
            </button>
            {inv.status === 'draft' && (
              <button onClick={confirm} className="btn-brand flex items-center gap-2" data-testid="confirm-invoice-btn">
                <CheckCircle2 size={14} /> Confirm &amp; Send
              </button>
            )}
            {isFinalized && inv.status !== 'paid' && (
              <>
                <button onClick={resend} className="btn-ghost flex items-center gap-2" data-testid="resend-invoice-btn">
                  <Send size={14} /> Resend
                </button>
                <button onClick={sendReminder} className="btn-ghost flex items-center gap-2" data-testid="send-reminder-btn">
                  <Mail size={14} /> Reminder
                </button>
                <button onClick={syncStripe} className="btn-ghost flex items-center gap-2" data-testid="sync-stripe-btn" title="Check Stripe — apply payment if it succeeded but didn't sync">
                  <CreditCard size={14} /> Sync Stripe
                </button>
                <button
                  onClick={() => {
                    setPayAmt(balance);
                    setShowPay(true);
                  }}
                  className="btn-brand"
                  data-testid="mark-paid-btn"
                >
                  Record Payment
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Total" value={money(inv.total)} />
        <SummaryCard label="Paid" value={money(inv.paid_amount)} tint="green" />
        <SummaryCard label="Balance" value={money(balance)} tint="red" />
        <SummaryCard label="Profit" value={money(inv.total_profit)} tint="blue" />
      </div>

      {/* View or Edit mode */}
      {!editing ? (
        <div className="card overflow-hidden">
          <div className="p-5 border-b border-[#f3f3f3]">
            <div className="text-xs font-semibold text-[#6b7280] uppercase tracking-wide mb-1">Items</div>
          </div>
          {/* Desktop table */}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Description</th>
                <th className="text-right px-4 py-2">Qty</th>
                <th className="text-right px-4 py-2">Price</th>
                <th className="text-right px-4 py-2">Cost</th>
                <th className="text-right px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={i} className="border-t border-[#f3f3f3]">
                  <td className="px-4 py-3 font-medium">{it.name}</td>
                  <td className="text-right px-4 py-3 mono">{it.qty}</td>
                  <td className="text-right px-4 py-3 mono">{money(it.unit_price)}</td>
                  <td className="text-right px-4 py-3 mono text-[#6b7280]">{money(it.unit_cost)}</td>
                  <td className="text-right px-4 py-3 mono font-semibold">{money(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#f3f3f3]">
            {(inv.items || []).map((it, i) => (
              <div key={i} className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{it.name}</div>
                    <div className="text-xs text-[#6b7280] mono mt-0.5">Qty {it.qty} × {money(it.unit_price)}</div>
                  </div>
                  <div className="mono font-semibold text-sm shrink-0">{money(it.line_total)}</div>
                </div>
                <div className="text-[11px] text-[#6b7280] mono">cost {money(it.unit_cost)}/unit</div>
              </div>
            ))}
          </div>
          <div className="p-5 border-t border-[#f3f3f3] flex justify-between items-center text-sm flex-wrap gap-3">
            <div className="text-[#6b7280]">
              Issue Date: <span className="text-[#0a0a0a] mono">{fmtDate(inv.issue_date)}</span> · Due Date:{' '}
              <span className="text-[#0a0a0a] mono">{fmtDate(inv.due_date)}</span>
            </div>
            {inv.notes && <div className="text-[#6b7280] italic text-xs max-w-md">"{inv.notes}"</div>}
          </div>
        </div>
      ) : (
        <div className="card p-5 space-y-4" data-testid="edit-invoice-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Edit Invoice</h3>
            {isFinalized && (
              <div className="text-xs text-[#d92027] bg-[#fef3f2] px-3 py-1 rounded-full">
                This invoice was already sent. After saving, click "Resend" to email the updated PDF.
              </div>
            )}
          </div>
          <div className="space-y-2">
            {editItems.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-12 md:col-span-5">
                  <select
                    value={it.product_id || ''}
                    onChange={(e) => updateLine(i, 'product_id', e.target.value)}
                    className="w-full px-2 py-1.5 border border-[#e7e7e7] rounded text-sm"
                    data-testid={`edit-prod-${i}`}
                  >
                    <option value="">— custom line —</option>
                    {products.map((p) => (
                      <option key={p.product_id} value={p.product_id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {!it.product_id && (
                    <input
                      type="text"
                      value={it.name}
                      onChange={(e) => updateLine(i, 'name', e.target.value)}
                      placeholder="Custom line name"
                      className="w-full mt-1 px-2 py-1.5 border border-[#e7e7e7] rounded text-sm"
                    />
                  )}
                </div>
                <div className="col-span-4 md:col-span-2">
                  <input
                    type="number"
                    min="1"
                    value={it.qty}
                    onChange={(e) => updateLine(i, 'qty', e.target.value)}
                    placeholder="Qty"
                    className="w-full px-2 py-1.5 border border-[#e7e7e7] rounded text-sm mono"
                    data-testid={`edit-qty-${i}`}
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={it.unit_price}
                    onChange={(e) => updateLine(i, 'unit_price', e.target.value)}
                    placeholder="Unit $"
                    className="w-full px-2 py-1.5 border border-[#e7e7e7] rounded text-sm mono"
                    data-testid={`edit-price-${i}`}
                  />
                </div>
                <div className="col-span-3 md:col-span-2 text-right mono font-semibold">
                  {money((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}
                </div>
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => removeLine(i)}
                    className="text-[#d92027] p-1"
                    data-testid={`edit-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
            <button onClick={addLine} className="btn-ghost text-sm flex items-center gap-1" data-testid="edit-add-line">
              <Plus size={14} /> Add line
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#6b7280] mb-1">Due date</label>
              <input
                type="date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#6b7280] mb-1">Notes</label>
              <input
                type="text"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-[#f3f3f3]">
            <div>
              <div className="text-xs uppercase text-[#6b7280]">New Total</div>
              <div className="text-2xl font-bold mono">{money(editSubtotal)}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={cancelEdit} className="btn-ghost">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-brand disabled:opacity-50"
                data-testid="save-invoice-edit-btn"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment history */}
      {inv.payments?.length > 0 && (
        <div className="card p-5">
          <h2 className="font-semibold mb-3">Payment History</h2>
          <div className="space-y-2">
            {inv.payments.map((p) => (
              <div
                key={p.payment_id}
                className="flex items-center justify-between py-2 border-b border-[#f3f3f3] last:border-0"
              >
                <div>
                  <div className="text-sm font-medium">{p.method}</div>
                  <div className="text-xs text-[#6b7280]">
                    {fmtDate(p.created_at)} · {p.note || '—'}
                  </div>
                </div>
                <div className="font-semibold mono text-green-700">{money(p.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showPay && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPay(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="mark-paid-modal"
          >
            <div className="flex justify-between">
              <h2 className="text-xl font-semibold">Record Payment</h2>
              <button onClick={() => setShowPay(false)}>
                <X size={20} />
              </button>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#6b7280] mb-1">Amount</div>
              <input
                type="number"
                step="0.01"
                value={payAmt}
                onChange={(e) => setPayAmt(e.target.value)}
                className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-lg mono font-semibold"
                data-testid="pay-amount-input"
              />
              <div className="text-xs text-[#6b7280] mt-1">
                Balance: <span className="mono">{money(balance)}</span>
                <button
                  onClick={() => setPayAmt(balance)}
                  className="ml-2 text-[#d92027] underline"
                >
                  Full
                </button>
                <button
                  onClick={() => setPayAmt(Math.round(balance / 2 * 100) / 100)}
                  className="ml-2 text-[#d92027] underline"
                >
                  Half
                </button>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#6b7280] mb-1">Method</div>
              <select
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
                className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg"
                data-testid="pay-method-select"
              >
                <option>Bank Transfer</option>
                <option>Cash</option>
                <option>Check</option>
                <option>Stripe</option>
                <option>Credit Card</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#6b7280] mb-1">Reference #</div>
              <input
                type="text"
                value={payReference}
                onChange={(e) => setPayReference(e.target.value)}
                placeholder="Check # / transaction ID (optional)"
                className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm"
                data-testid="pay-reference-input"
              />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#6b7280] mb-1">Note</div>
              <input
                type="text"
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                placeholder="Optional note"
                className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm"
                data-testid="pay-note-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowPay(false)} className="btn-ghost">
                Cancel
              </button>
              <button onClick={markPaid} className="btn-brand" data-testid="confirm-pay-btn">
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tint }) {
  const tints = { green: 'text-green-700', red: 'text-[#d92027]', blue: 'text-blue-700' };
  return (
    <div className="card p-4">
      <div className="text-[10px] text-[#6b7280] uppercase tracking-wider font-semibold">{label}</div>
      <div className={`text-xl sm:text-2xl font-semibold mono ${tints[tint] || ''}`}>{value}</div>
    </div>
  );
}
